import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { Search, Package, Truck, CheckCircle, Clock, MapPin, ExternalLink, AlertCircle, Loader2, XCircle, Lock } from 'lucide-react';

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Pending' },
  processing: { icon: Package, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Processing' },
  shipped: { icon: Truck, color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'Shipped' },
  delivered: { icon: CheckCircle, color: 'text-accent-success', bg: 'bg-accent-success/10', label: 'Delivered' },
  cancelled: { icon: XCircle, color: 'text-accent-error', bg: 'bg-accent-error/10', label: 'Cancelled' },
  unfulfilled: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Processing' },
};

const LULU_STATUS_LABELS = {
  CREATED: 'Order created — preparing for print',
  UNPAID: 'Awaiting payment confirmation',
  PAYMENT_IN_PROGRESS: 'Payment being processed',
  PRODUCTION_READY: 'Ready for production',
  PRODUCTION_DELAYED: 'Production temporarily delayed',
  IN_PRODUCTION: 'Your book is being printed',
  SHIPPED: 'Shipped — on its way to you',
  IN_TRANSIT: 'In transit',
  DELIVERED: 'Delivered',
  CANCELED: 'Cancelled',
  REJECTED: 'Order rejected',
};

const DELIVERY_LABELS = {
  local_golan: 'Local Delivery (Golan Heights)',
  golan_region: 'Golan Heights Delivery',
  israel_standard: 'Israel Shipping',
  international_lulu: 'International (Print & Ship)',
  pickup: 'Pickup',
  digital: 'Digital Download',
};

// Demo orders shown when the user clicks "View Demo" — 3 realistic samples
// covering the main delivery + status states. No backend call is made.
const DEMO_ORDERS = [
  {
    paypal_order_id: 'DEMO-INTL-9F72',
    item_name: 'Center of Gravity — Hardcover (International)',
    amount: 49.0,
    currency: 'USD',
    delivery_type: 'international_lulu',
    fulfillment_status: 'shipped',
    lulu_status: 'SHIPPED',
    lulu_live_status: { name: 'SHIPPED', message: 'Your book left our print partner — en route to you.' },
    tracking: { carrier: 'DHL', tracking_id: 'JD014600005712345678', tracking_url: 'https://www.dhl.com/en/express/tracking.html?AWB=JD014600005712345678' },
    shipping_address: { name: 'Sample Reader', address1: '221B Baker Street', city: 'London', state: '', zip: 'NW1 6XE', country_code: 'GB' },
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
    _demo: true,
  },
  {
    paypal_order_id: 'DEMO-LOCAL-3A11',
    item_name: 'Center of Gravity — Paperback (Golan Pickup)',
    amount: 70.0,
    currency: 'ILS',
    delivery_type: 'pickup',
    fulfillment_status: 'delivered',
    shipping_address: { name: 'ضيف تجريبي', address1: 'Majdal Shams Pickup Point', city: 'Majdal Shams', state: '', zip: '', country_code: 'IL' },
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
    _demo: true,
  },
  {
    paypal_order_id: 'DEMO-DIGITAL-7Z88',
    item_name: 'Center of Gravity — Digital Edition (PDF)',
    amount: 9.99,
    currency: 'USD',
    delivery_type: 'digital',
    fulfillment_status: 'delivered',
    shipping_address: null,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    _demo: true,
  },
];

function OrderCard({ order }) {
  const st = STATUS_CONFIG[order.fulfillment_status] || STATUS_CONFIG.pending;
  const StIcon = st.icon;
  const luluStatus = order.lulu_status || order.lulu_live_status?.name;
  const hasTracking = order.tracking?.tracking_url;

  return (
    <div className="glass-card rounded-2xl p-6 space-y-4" data-testid={`tracked-order-${order.paypal_order_id}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${st.bg} flex items-center justify-center`}>
            <StIcon size={20} className={st.color} />
          </div>
          <div>
            <h3 className="font-body font-semibold text-heading text-sm">{order.item_name}</h3>
            <p className="text-text-muted text-xs font-body">Order: {order.paypal_order_id}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${st.bg} ${st.color}`}>
          {st.label}
        </span>
      </div>

      {/* Progress Bar for Lulu orders */}
      {order.delivery_type === 'international_lulu' && (
        <div data-testid="lulu-progress">
          <LuluProgress status={luluStatus} />
        </div>
      )}

      {/* Lulu Status Detail */}
      {luluStatus && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-sacred-gold/5 border border-sacred-gold/10">
          <Package size={14} className="text-sacred-gold mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-body text-sm text-heading font-medium">
              {LULU_STATUS_LABELS[luluStatus] || luluStatus}
            </p>
            {order.lulu_live_status?.message && (
              <p className="text-text-muted text-xs font-body mt-0.5">{order.lulu_live_status.message}</p>
            )}
          </div>
        </div>
      )}

      {/* Tracking Link */}
      {hasTracking && (
        <a
          href={order.tracking.tracking_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-3 rounded-lg bg-sacred-gold/10 border border-sacred-gold/20 hover:bg-sacred-gold/15 transition-colors group"
          data-testid="tracking-link"
        >
          <Truck size={16} className="text-sacred-gold" />
          <div className="flex-1">
            <p className="font-body text-sm text-heading font-medium">Track Your Package</p>
            <p className="text-text-muted text-xs font-body">
              {order.tracking.carrier && `${order.tracking.carrier} — `}
              {order.tracking.tracking_id}
            </p>
          </div>
          <ExternalLink size={14} className="text-sacred-gold group-hover:translate-x-0.5 transition-transform" />
        </a>
      )}

      {/* Order Details */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm font-body">
        <div>
          <p className="text-text-muted/60 text-xs">Amount</p>
          <p className="text-heading font-medium">
            {order.currency === 'ILS' ? '\u20AA' : '$'}{order.amount?.toFixed?.(2) || order.amount}
          </p>
        </div>
        <div>
          <p className="text-text-muted/60 text-xs">Delivery</p>
          <p className="text-heading">{DELIVERY_LABELS[order.delivery_type] || order.delivery_type}</p>
        </div>
        <div>
          <p className="text-text-muted/60 text-xs">Date</p>
          <p className="text-heading">{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</p>
        </div>
      </div>

      {/* Shipping Address */}
      {order.shipping_address?.city && (
        <div className="flex items-start gap-2 text-text-muted/70 text-xs font-body">
          <MapPin size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            {[order.shipping_address.name, order.shipping_address.address1, order.shipping_address.city,
              order.shipping_address.state, order.shipping_address.zip, order.shipping_address.country_code]
              .filter(Boolean).join(', ')}
          </span>
        </div>
      )}
    </div>
  );
}

function LuluProgress({ status }) {
  const steps = ['CREATED', 'IN_PRODUCTION', 'SHIPPED', 'DELIVERED'];
  const stepLabels = ['Ordered', 'Printing', 'Shipped', 'Delivered'];
  const currentIdx = steps.indexOf(status);
  const normalizedIdx = status === 'PRODUCTION_READY' || status === 'PAYMENT_IN_PROGRESS' || status === 'UNPAID' ? 0
    : status === 'IN_PRODUCTION' || status === 'PRODUCTION_DELAYED' ? 1
    : status === 'SHIPPED' || status === 'IN_TRANSIT' ? 2
    : status === 'DELIVERED' ? 3
    : currentIdx >= 0 ? currentIdx : 0;

  return (
    <div className="flex items-center gap-1">
      {steps.map((_, i) => (
        <div key={i} className="flex-1 flex items-center gap-1">
          <div className={`w-full h-1.5 rounded-full transition-colors ${i <= normalizedIdx ? 'bg-sacred-gold' : 'bg-sacred-gold/15'}`} />
        </div>
      ))}
      <div className="flex justify-between w-full absolute" style={{ display: 'none' }} />
    </div>
  );
}

export default function OrderTracking() {
  const { lang } = useLang();
  const { user } = useAuth();
  const toast = useGravityToast();
  const [searchParams] = useSearchParams();

  const [searchType, setSearchType] = useState('order_id');
  const [searchValue, setSearchValue] = useState(searchParams.get('order_id') || '');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [demoMode, setDemoMode] = useState(false);
  const [autoLoaded, setAutoLoaded] = useState(false);

  const loadDemo = () => {
    setError('');
    setSearched(true);
    setOrders(DEMO_ORDERS);
    setDemoMode(true);
  };

  const clearDemo = () => {
    setOrders([]);
    setSearched(false);
    setDemoMode(false);
    setSearchValue('');
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchValue.trim()) return;
    setLoading(true);
    setError('');
    setSearched(true);
    setDemoMode(false);
    try {
      const params = searchType === 'order_id'
        ? { order_id: searchValue.trim() }
        : { email: searchValue.trim() };
      const res = await api.get('/orders/track', { params });
      setOrders(res.data || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setOrders([]);
      } else {
        setError(err.response?.data?.detail || 'Failed to look up order');
      }
    }
    setLoading(false);
  };

  // Auto-search if order_id in URL (from email links etc.)
  useEffect(() => {
    const oid = searchParams.get('order_id');
    if (!oid) return;
    setSearchValue(oid);
    setSearchType('order_id');
    setLoading(true);
    setSearched(true);
    api.get('/orders/track', { params: { order_id: oid } })
      .then(res => setOrders(res.data || []))
      .catch(err => {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || 'Failed to look up order');
        } else {
          setOrders([]);
        }
      })
      .finally(() => setLoading(false));
  }, [searchParams]);

  // Signed-in users: silently auto-load their orders by email
  useEffect(() => {
    if (!user?.email || autoLoaded) return;
    if (searchParams.get('order_id')) return; // URL takes precedence
    setAutoLoaded(true);
    setLoading(true);
    setSearched(true);
    setSearchType('email');
    setSearchValue(user.email);
    api.get('/orders/track', { params: { email: user.email } })
      .then(res => setOrders(res.data || []))
      .catch(err => {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || 'Failed to load your orders');
        } else {
          setOrders([]);
        }
      })
      .finally(() => setLoading(false));
  }, [user, autoLoaded, searchParams]);

  return (
    <div className="min-h-screen py-20 md:py-32 px-4" data-testid="order-tracking-page">
      <div className="max-w-3xl mx-auto">
        <ScrollReveal>
          <h1 className="text-4xl sm:text-5xl font-display text-heading mb-3" data-testid="tracking-title">
            {lang === 'ar' ? 'تتبع طلبك' : 'Track Your Order'}
          </h1>
          <p className="text-text-muted font-body mb-6">
            {user
              ? (lang === 'ar' ? `مرحبًا ${user.display_name || ''} — هذه هي طلباتك.` : `Welcome back${user.display_name ? `, ${user.display_name}` : ''} — here are your orders.`)
              : (lang === 'ar' ? 'أدخل رقم طلبك أو بريدك الإلكتروني لتتبع حالة طلبك.' : 'Enter your order ID or email to check the status of your purchase.')}
          </p>
          {!user && (
            <div
              className="glass-card rounded-2xl p-4 mb-6 flex items-center justify-between gap-3 flex-wrap"
              data-testid="tracking-signin-nudge"
              style={{ border: '1px solid rgba(200,165,92,0.25)' }}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-sacred-gold/10 flex items-center justify-center flex-shrink-0">
                  <Lock size={14} className="text-sacred-gold" />
                </div>
                <div>
                  <p className="text-heading font-body text-sm font-semibold">
                    {lang === 'ar' ? 'لديك حساب؟ سجّل دخولك' : 'Have an account? Sign in'}
                  </p>
                  <p className="text-text-muted font-body text-xs">
                    {lang === 'ar' ? 'نعرضها لك فورًا دون الحاجة لإدخال رقم الطلب.' : 'We\'ll show all your orders instantly — no order ID needed.'}
                  </p>
                </div>
              </div>
              <Link
                to="/auth"
                className="gold-button px-5 py-2 rounded-full font-body text-xs inline-flex items-center gap-1.5 shrink-0"
                data-testid="tracking-signin-btn"
              >
                {lang === 'ar' ? 'تسجيل الدخول' : 'Sign In'}
              </Link>
            </div>
          )}
        </ScrollReveal>

        {/* Search */}
        <ScrollReveal delay={0.05}>
          <form onSubmit={handleSearch} className="glass-card rounded-2xl p-6 mb-8" data-testid="tracking-search">
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setSearchType('order_id')}
                className={`px-4 py-2 rounded-full font-body text-sm transition-all ${searchType === 'order_id' ? 'gold-button' : 'glass-button text-text-muted'}`}
                data-testid="search-by-order"
              >
                Order ID
              </button>
              <button
                type="button"
                onClick={() => setSearchType('email')}
                className={`px-4 py-2 rounded-full font-body text-sm transition-all ${searchType === 'email' ? 'gold-button' : 'glass-button text-text-muted'}`}
                data-testid="search-by-email"
              >
                Email
              </button>
            </div>
            <div className="flex gap-3">
              <input
                type={searchType === 'email' ? 'email' : 'text'}
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                placeholder={searchType === 'order_id' ? 'Enter your PayPal order ID...' : 'Enter your email address...'}
                className="flex-1 px-4 py-3 rounded-xl glass-input font-body text-sm"
                data-testid="tracking-input"
                required
              />
              <button
                type="submit"
                disabled={loading || !searchValue.trim()}
                className="gold-button px-6 py-3 rounded-xl font-body text-sm flex items-center gap-2 disabled:opacity-50"
                data-testid="tracking-search-btn"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                {lang === 'ar' ? 'بحث' : 'Search'}
              </button>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-sacred-gold/10">
              <p className="text-text-muted/70 font-body text-xs">
                {lang === 'ar' ? 'لا يوجد طلب بعد؟ جرّب العرض التوضيحي لترى كيف تبدو الصفحة.' : 'No orders yet? Try the demo to see how tracking looks.'}
              </p>
              {demoMode ? (
                <button
                  type="button"
                  onClick={clearDemo}
                  className="text-xs font-body px-3 py-1.5 rounded-full glass-button text-text-muted hover:text-sacred-gold"
                  data-testid="exit-demo-btn"
                >
                  {lang === 'ar' ? 'خروج من العرض' : 'Exit Demo'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={loadDemo}
                  className="text-xs font-body px-3 py-1.5 rounded-full bg-sacred-gold/10 text-sacred-gold border border-sacred-gold/20 hover:bg-sacred-gold/20"
                  data-testid="view-demo-btn"
                >
                  {lang === 'ar' ? 'عرض توضيحي' : 'View Demo'}
                </button>
              )}
            </div>
          </form>
        </ScrollReveal>

        {/* Demo banner */}
        {demoMode && (
          <div
            className="mb-6 p-4 rounded-xl bg-sacred-gold/10 border border-sacred-gold/30 flex items-center gap-3"
            data-testid="demo-banner"
          >
            <AlertCircle size={16} className="text-sacred-gold flex-shrink-0" />
            <p className="text-heading font-body text-xs">
              {lang === 'ar'
                ? 'هذا عرض توضيحي. الطلبات أدناه ليست حقيقية — فقط لتوضيح شكل الصفحة لطلبات فعلية.'
                : 'This is a demo. The orders below are samples — they show how real tracking will look.'}
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-accent-error/5 border border-accent-error/15 flex items-center gap-2" data-testid="tracking-error">
            <AlertCircle size={16} className="text-accent-error" />
            <p className="text-accent-error font-body text-sm">{error}</p>
          </div>
        )}

        {/* Results */}
        {searched && !loading && orders.length === 0 && !error && (
          <ScrollReveal>
            <div className="glass-card rounded-2xl p-8 text-center">
              <Package size={48} className="text-sacred-gold/20 mx-auto mb-4" />
              <h3 className="font-display text-lg text-heading mb-2">
                {user
                  ? (lang === 'ar' ? 'لا توجد طلبات بعد' : 'No orders yet')
                  : (lang === 'ar' ? 'لم يتم العثور على طلبات' : 'No Orders Found')}
              </h3>
              <p className="text-text-muted font-body text-sm">
                {user
                  ? (lang === 'ar' ? 'عندما تشتري شيئًا ستظهر طلباتك هنا.' : 'When you place an order, it will show up here automatically.')
                  : (lang === 'ar' ? 'تأكد من إدخال رقم الطلب أو البريد الإلكتروني الصحيح.' : 'Make sure you entered the correct order ID or email address.')}
              </p>
            </div>
          </ScrollReveal>
        )}

        {orders.length > 0 && (
          <div className="space-y-4">
            {orders.map((order, i) => (
              <ScrollReveal key={order.paypal_order_id || i} delay={i * 0.05}>
                <OrderCard order={order} />
              </ScrollReveal>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-sacred-gold" />
          </div>
        )}
      </div>
    </div>
  );
}
